import type { ReactNode } from "react";
import { ExternalLink } from "lucide-react";
import type { AutomationResultRecord } from "@/lib/api/generated/control-api.schemas";
import { Badge } from "@/components/ui/badge";
import { createAutomationResultViewModel } from "./automation-result-view-model";

interface AutomationResultContentProps {
  result: AutomationResultRecord;
}

function stringifyJson(value: unknown) {
  if (!value) {
    return null;
  }

  return JSON.stringify(value, null, 2);
}

export function AutomationResultContent({
  result,
}: AutomationResultContentProps) {
  const viewModel = createAutomationResultViewModel(result);
  const payloadJson = stringifyJson(viewModel.payload);
  const workflowResultJson = stringifyJson(viewModel.workflowResult);
  const answerContent = viewModel.content ?? viewModel.summary;

  return (
    <div className="grid gap-5">
      <section className="grid max-w-[78ch] gap-3">
        {viewModel.title ? (
          <h2
            className="text-xl font-semibold leading-7"
            style={{ color: "var(--text-strong)" }}
          >
            {viewModel.title}
          </h2>
        ) : null}
        <MarkdownResult markdown={answerContent} />
      </section>

      {viewModel.digestItems.length ? (
        <section className="grid gap-3">
          <div className="flex items-center justify-between gap-3">
            <h3
              className="text-sm font-semibold"
              style={{ color: "var(--text-strong)" }}
            >
              Referenced Items
            </h3>
            <Badge variant="outline">
              {viewModel.digestItems.length} items
            </Badge>
          </div>
          <div className="grid gap-2">
            {viewModel.digestItems.map((item, index) => (
              <article
                key={`${item.title}:${item.url ?? index}`}
                className="rounded-md border p-3"
                style={{
                  borderColor: "var(--border)",
                  background: "var(--bg)",
                }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    {item.url ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-start gap-1.5 text-sm font-medium hover:underline"
                        style={{ color: "var(--text-strong)" }}
                      >
                        <span>{item.title}</span>
                        <ExternalLink className="mt-0.5 size-3.5 shrink-0" />
                      </a>
                    ) : (
                      <div
                        className="text-sm font-medium"
                        style={{ color: "var(--text-strong)" }}
                      >
                        {item.title}
                      </div>
                    )}
                    {item.takeaway ? (
                      <p
                        className="mt-1 text-sm leading-6"
                        style={{ color: "var(--text)" }}
                      >
                        {item.takeaway}
                      </p>
                    ) : null}
                  </div>
                  {item.source ? (
                    <Badge variant="draft" className="shrink-0">
                      {item.source}
                    </Badge>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {payloadJson || workflowResultJson ? (
        <details
          className="rounded-md border"
          style={{ borderColor: "var(--border)" }}
        >
          <summary
            className="cursor-pointer px-3 py-2 text-xs font-medium"
            style={{ color: "var(--muted-foreground)" }}
          >
            Technical details
          </summary>
          <div
            className="grid gap-3 border-t p-3"
            style={{ borderColor: "var(--border)" }}
          >
            {payloadJson ? (
              <JsonBlock title="Payload JSON" value={payloadJson} />
            ) : null}
            {workflowResultJson ? (
              <JsonBlock title="Workflow JSON" value={workflowResultJson} />
            ) : null}
          </div>
        </details>
      ) : null}
    </div>
  );
}

type MarkdownBlock =
  | { kind: "heading"; level: 1 | 2 | 3 | 4; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "list"; ordered: boolean; items: string[] }
  | { kind: "quote"; text: string };

function MarkdownResult({ markdown }: { markdown: string }) {
  const blocks = parseMarkdownBlocks(markdown);

  return (
    <div className="grid gap-3 text-sm leading-6" style={{ color: "var(--text)" }}>
      {blocks.map((block, index) => renderMarkdownBlock(block, index))}
    </div>
  );
}

function parseMarkdownBlocks(markdown: string): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = [];
  const paragraphLines: string[] = [];
  let activeList: { ordered: boolean; items: string[] } | null = null;

  const flushParagraph = () => {
    if (paragraphLines.length === 0) {
      return;
    }
    blocks.push({
      kind: "paragraph",
      text: paragraphLines.join(" ").trim(),
    });
    paragraphLines.length = 0;
  };

  const flushList = () => {
    if (!activeList) {
      return;
    }
    blocks.push({ kind: "list", ordered: activeList.ordered, items: activeList.items });
    activeList = null;
  };

  for (const rawLine of markdown.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }

    const heading = /^(#{1,4})\s+(.+)$/.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({
        kind: "heading",
        level: heading[1].length as 1 | 2 | 3 | 4,
        text: heading[2].trim(),
      });
      continue;
    }

    const ordered = /^\d+\.\s+(.+)$/.exec(line);
    const unordered = /^[-*]\s+(.+)$/.exec(line);
    const listItem = ordered?.[1] ?? unordered?.[1];
    if (listItem) {
      flushParagraph();
      const isOrdered = Boolean(ordered);
      if (!activeList || activeList.ordered !== isOrdered) {
        flushList();
        activeList = { ordered: isOrdered, items: [] };
      }
      activeList.items.push(listItem.trim());
      continue;
    }

    const quote = /^>\s?(.+)$/.exec(line);
    if (quote) {
      flushParagraph();
      flushList();
      blocks.push({ kind: "quote", text: quote[1].trim() });
      continue;
    }

    flushList();
    paragraphLines.push(line);
  }

  flushParagraph();
  flushList();
  return blocks.length ? blocks : [{ kind: "paragraph", text: markdown }];
}

function renderMarkdownBlock(block: MarkdownBlock, index: number) {
  switch (block.kind) {
    case "heading": {
      const className =
        block.level <= 2
          ? "text-base font-semibold leading-7"
          : "text-sm font-semibold leading-6";
      return (
        <h3
          key={`${block.kind}:${index}:${block.text}`}
          className={className}
          style={{ color: "var(--text-strong)" }}
        >
          {renderInlineMarkdown(block.text)}
        </h3>
      );
    }
    case "list": {
      const ListTag = block.ordered ? "ol" : "ul";
      return (
        <ListTag
          key={`${block.kind}:${index}`}
          className={`grid gap-1 pl-5 ${
            block.ordered ? "list-decimal" : "list-disc"
          }`}
        >
          {block.items.map((item, itemIndex) => (
            <li key={`${item}:${itemIndex}`}>{renderInlineMarkdown(item)}</li>
          ))}
        </ListTag>
      );
    }
    case "quote":
      return (
        <blockquote
          key={`${block.kind}:${index}:${block.text}`}
          className="border-l-2 pl-3"
          style={{
            borderColor: "var(--border)",
            color: "var(--muted-foreground)",
          }}
        >
          {renderInlineMarkdown(block.text)}
        </blockquote>
      );
    case "paragraph":
      return (
        <p key={`${block.kind}:${index}:${block.text}`}>
          {renderInlineMarkdown(block.text)}
        </p>
      );
  }
}

function renderInlineMarkdown(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const inlinePattern =
    /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)|\*\*([^*]+)\*\*|`([^`]+)`/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = inlinePattern.exec(text)) !== null) {
    if (match.index > cursor) {
      nodes.push(text.slice(cursor, match.index));
    }

    if (match[1] && match[2]) {
      nodes.push(
        <a
          key={`${match[2]}:${match.index}`}
          href={match[2]}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 font-medium underline-offset-2 hover:underline"
          style={{ color: "var(--text-strong)" }}
        >
          {match[1]}
          <ExternalLink className="size-3" />
        </a>,
      );
    } else if (match[3]) {
      nodes.push(
        <strong
          key={`strong:${match.index}:${match[3]}`}
          className="font-semibold"
          style={{ color: "var(--text-strong)" }}
        >
          {match[3]}
        </strong>,
      );
    } else if (match[4]) {
      nodes.push(
        <code
          key={`code:${match.index}:${match[4]}`}
          className="rounded border px-1 py-0.5 text-[0.92em]"
          style={{ borderColor: "var(--border)", background: "var(--bg)" }}
        >
          {match[4]}
        </code>,
      );
    }
    cursor = match.index + match[0].length;
  }

  if (cursor < text.length) {
    nodes.push(text.slice(cursor));
  }

  return nodes.length ? nodes : [text];
}

function JsonBlock({ title, value }: { title: string; value: string }) {
  return (
    <section className="grid gap-2">
      <h4
        className="text-xs font-semibold"
        style={{ color: "var(--text-strong)" }}
      >
        {title}
      </h4>
      <pre
        className="max-h-80 overflow-auto rounded-md border p-3 text-xs"
        style={{ borderColor: "var(--border)" }}
      >
        {value}
      </pre>
    </section>
  );
}
